import { createSignal } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import type { Page } from "@apunte-digital/powersync";
import {
  createPageMutation,
  movePageMutation,
  movePageToTrashMutation,
  restorePageVersionMutation,
  updatePageMutation,
} from "@apunte-digital/shared";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";

export function usePageMutations() {
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const navigate = useNavigate();

  const [pageToDelete, setPageToDelete] = createSignal<Page | null>(null);

  // The local DB (PowerSync/Drizzle) loads asynchronously at startup.
  // Until then every mutation is a no-op; this signal lets action buttons
  // be disabled instead of failing silently.
  const ready = () => Boolean(drizzle());

  const handleCreatePage = async (opts?: { parentPageId?: string; afterSortKey?: string }) => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid) {
      return;
    }
    try {
      const id = await createPageMutation(dbInstance, uid, {
        parentPageId: opts?.parentPageId,
        afterSortKey: opts?.afterSortKey ?? null,
      });
      void navigate({ to: "/notes/$pageId", params: { pageId: id } });
    } catch (err: unknown) {
      console.error("Error creating page:", err instanceof Error ? err.message : String(err));
    }
  };

  const handleRenamePage = async (id: string, title: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await updatePageMutation(dbInstance, id, { title });
    } catch (err: unknown) {
      console.error("Error renaming page:", err instanceof Error ? err.message : String(err));
    }
  };

  const handleMovePage = async (
    pageId: string,
    target: {
      parentPageId: string | null;
      afterSortKey?: string | null;
      beforeSortKey?: string | null;
    },
  ) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await movePageMutation(dbInstance, pageId, target);
    } catch (err: unknown) {
      console.error("Error moving page:", err instanceof Error ? err.message : String(err));
    }
  };

  const handleSetPageIcon = async (id: string, icon: string | null) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await updatePageMutation(dbInstance, id, { icon });
    } catch (err: unknown) {
      console.error("Error setting page icon:", err instanceof Error ? err.message : String(err));
    }
  };

  // Trash (L2): "deleting" a page is a recoverable soft-delete; physical
  // deletion lives only in the trash ("delete permanently").
  const executeMoveToTrash = async (page: Page) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await movePageToTrashMutation(dbInstance, page.id);
      setPageToDelete(null);
      void navigate({ to: "/notes" });
    } catch (err: unknown) {
      console.error("Error trashing page:", err instanceof Error ? err.message : String(err));
    }
  };

  // History (L4): restoring a version = a normal forward write
  // (the server archives the current state as a new version before applying it).
  const executeRestoreVersion = async (
    pageId: string,
    version: Parameters<typeof restorePageVersionMutation>[2],
  ) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await restorePageVersionMutation(dbInstance, pageId, version);
    } catch (err: unknown) {
      console.error("Error restoring version:", err instanceof Error ? err.message : String(err));
    }
  };

  return {
    ready,
    handleCreatePage,
    handleRenamePage,
    handleMovePage,
    handleSetPageIcon,
    pageToDelete,
    setPageToDelete,
    executeMoveToTrash,
    executeRestoreVersion,
  };
}
